import React, { useRef } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Container } from 'components/base';
import {
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  WIDTH_LADDERS,
  getCloudinarySrcSet,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import { getDefaultSetCount, getPagePerSet } from 'data/lib/product';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { discountedPrice } from 'ecommerce/lib/coupon';

import Breadcrumb from '../components/breadcrumb';
import { Picture, Responsive, ctaTracker, productImage } from '../components/landing/shared';
import StickyBar from '../components/landing/stickyBar';
import { Head, Layout } from '../components/layout';
import NewsletterTile from '../components/newsletter/newsletterTile';
import { getEditorPageLink } from '../components/pageLinks';
import PdpIcon from '../components/product/pdpIcon';
import ProductFaq from '../components/product/productFaq';
import { money } from '../lib/landingPrice';
import useInView from '../lib/useInView';
import useScrolledPast from '../lib/useScrolledPast';
import SETTINGS from '../settings';

// Mini Photo Books (docs/mini-photo-books.md). Landing page for the 6 × 6 Softcover Photo Book,
// built from the Pencil frames "Mini Photo Books — New Page" / "— Mobile" / "— Mobile (Sticky
// CTA, scrolled)". Prices, included pages and the extra sheet price come from the catalog SKU.
const track = ctaTracker('mini_photo_books', { size: '6x6' });

const SKU = {
  MINI: 'softcover-photo-book',
  LARGE: 'photolane-softcover-photo-book',
};

// Not in the catalog: the page limit comes from the book template, paper and captions from
// content/products/softcover-books.md. Recheck them with the product facts in the docs.
const MAX_PAGES = 100;

const ID = {
  IDEAS: 'ideas',
  DETAILS: 'details',
  GUIDE: 'guide',
  SIZES: 'sizes',
  FAQ: 'faq',
};

const HREF = {
  EDITOR: getEditorPageLink(SKU.MINI).as,
  SOFTCOVER: '/softcover-books',
  GUIDE: '/how-to-make-a-photo-book',
  SHIPPING: '/shipping',
};

// One label for every create button. Switch it here if the 6 × 6 editor route stops opening the
// right size (spec: "Choose the 6 × 6 book" plus an explicit size step).
const CTA_LABEL = 'Make a mini photo book';

const guide = (name) => ({
  imageKey: `landing/photo-book-guide/${name}`,
  widths: WIDTH_LADDERS.landing,
});

// Only 6-inch photos. softcover-books/1 and /4 show the 8 × 8 book or mixed sizes.
const IMAGES = {
  hero: productImage('softcover-books/2'),
  baby: productImage('softcover-books/6'),
  shelf: productImage('softcover-books/3'),
  coast: guide('coast'),
  beachWide: guide('beach-wide'),
  beachSelfie: guide('beach-selfie'),
  dunes: guide('dunes'),
  beachDog: guide('beach-dog'),
  tree: guide('tree'),
  sunset: guide('sunset'),
  group: guide('group'),
  grandpaBlossom: guide('grandpa-blossom'),
  grandpaGarden: guide('grandpa-garden'),
  baking: guide('baking'),
};

const HERO_SIZES = '(min-width: 992px) 600px, calc(100vw - 40px)';

const BREADCRUMB = [
  { name: 'Home', url: '/' },
  { name: 'Photo Books', url: '/photo-books' },
  { name: 'Mini Photo Books' },
];

// Drawn books for the idea cards and the final CTA. Ideas to borrow, not editor templates.
const COVERS = {
  coast: { image: IMAGES.coast, title: 'Two days on the coast' },
  friends: { image: IMAGES.group, title: 'Us, since 2016' },
  grandpa: { image: IMAGES.grandpaBlossom, title: 'For Grandpa' },
};

const PICK_LIST = [
  'One wide view of the place',
  'The people you went with',
  'A small detail from each day',
  'Place and date as captions',
];

const SMALL_IDEAS = [
  {
    key: 'friendship',
    title: 'A friendship',
    body: 'Gather the photos only the two of you would understand. Add the inside jokes as captions.',
    tone: 'mint',
    cover: COVERS.friends,
    pages: [
      { image: IMAGES.tree, tall: true },
      { image: IMAGES.sunset, caption: 'Ten years of Sunday coffee' },
    ],
  },
  {
    key: 'grandparents',
    title: 'A gift for grandparents',
    body: 'Pick the faces they ask about. Add names and ages under the photos, so every page is easy to share.',
    tone: 'cream',
    cover: COVERS.grandpa,
    pages: [
      { image: IMAGES.grandpaGarden, caption: 'Grandpa and Ava, June' },
      { image: IMAGES.baking, caption: 'Ava, age 6' },
    ],
  },
];

const ADVICE = [
  {
    title: 'Choose one theme',
    body: 'One trip, one friend, one ordinary month. If a photo doesn’t fit the theme, save it for the next book.',
  },
  {
    title: 'Gather the moments that tell it',
    body: 'A wide shot sets the scene, close ones show faces, a few details fill the gaps. Leave out near-duplicates.',
  },
  {
    title: 'Add captions where names or dates matter',
    body: 'Who was there, where, and when. A short line under the photo is enough, and it is the part people forget first.',
  },
];

const LAYOUT_NOTES = [
  {
    key: 'wide',
    icon: 'mountain',
    desktop: 'One wide shot sets the scene',
    phone: 'One wide shot',
  },
  { key: 'close', icon: 'copy', desktop: 'Two close ones, one moment', phone: 'Two close ones' },
  { key: 'place', icon: 'penLine', desktop: 'Caption: place and date', phone: 'Place and date' },
  { key: 'names', icon: 'penLine', desktop: 'Caption: names', phone: 'Names' },
];

// "25¢" for the extra sheet, money() for everything else.
const sheetPrice = (amount, currency) =>
  amount < 1 ? `${Math.round(amount * 100)}¢` : money(amount, currency);

// Starting price of one SKU with the claimed coupon applied, like the editor and the cart.
const skuPrice = (sku, previewSummary) => {
  if (!sku) return null;
  const discounted = previewSummary ? discountedPrice(previewSummary, sku.price, sku.id) : null;
  return {
    price: money(discounted ?? sku.price, sku.currency),
    originalPrice: discounted != null ? money(sku.price, sku.currency) : undefined,
  };
};

// Catalog facts for both sizes. Null fields hide the sentences that need them.
const useBookFacts = () => {
  const dataContext = useDataContext();
  const { previewSummary } = useClaimedCoupon();
  const mini = dataContext.getSkuProduct(SKU.MINI);
  const large = dataContext.getSkuProduct(SKU.LARGE);

  return {
    mini: skuPrice(mini, previewSummary),
    large: skuPrice(large, previewSummary),
    pages: mini ? getDefaultSetCount(mini) * getPagePerSet(mini) : null,
    sheetPrice: mini?.pricePerSet ? sheetPrice(mini.pricePerSet, mini.currency) : null,
  };
};

// "From $15", with the list price struck through while a claimed coupon covers the book.
const FromPrice = ({ price, className }) =>
  price ? (
    <span className={className}>
      From{' '}
      {price.originalPrice && (
        <>
          <span className="sr-only">original price </span>
          <s className="lnd-price-original">{price.originalPrice}</s>
          <span className="sr-only">, now</span>{' '}
        </>
      )}
      <span className={price.originalPrice ? 'lnd-price-sale' : undefined}>{price.price}</span>
    </span>
  ) : null;

FromPrice.propTypes = {
  price: PropTypes.shape({ price: PropTypes.string, originalPrice: PropTypes.string }),
  className: PropTypes.string,
};

const ArrowLink = ({ href, children, onClick, icon = 'arrowRight', className }) => (
  <Link href={href} className={classNames('mpb-link', className)} onClick={onClick}>
    {children}
    <PdpIcon name={icon} size={16} />
  </Link>
);

ArrowLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  className: PropTypes.string,
};

const CreateButton = ({ location, className }) => (
  <Link
    href={HREF.EDITOR}
    className={classNames('lnd-btn mpb-btn', className)}
    onClick={track('create', location, HREF.EDITOR)}
  >
    {CTA_LABEL}
    <PdpIcon name="arrowRight" size={18} />
  </Link>
);

CreateButton.propTypes = {
  location: PropTypes.string.isRequired,
  className: PropTypes.string,
};

// Drawn mockups. Photos inside are decorative, the copy next to them carries the meaning.
const Photo = ({ image, sizes = '240px', className }) => (
  <span className={classNames('mpb-photo', className)}>
    <Picture image={image} sizes={sizes} alt="" />
  </span>
);

Photo.propTypes = {
  image: PropTypes.object.isRequired,
  sizes: PropTypes.string,
  className: PropTypes.string,
};

const Cover = ({ cover, className }) => (
  <span className={classNames('mpb-cover', className)}>
    <Photo image={cover.image} />
    <span className="mpb-cover-title">{cover.title}</span>
  </span>
);

Cover.propTypes = {
  cover: PropTypes.shape({ image: PropTypes.object, title: PropTypes.string }).isRequired,
  className: PropTypes.string,
};

const Page = ({ photos, caption, className }) => (
  <span className={classNames('mpb-page', className)}>
    <span className="mpb-page-photos">
      {photos.map((photo) => (
        <Photo key={photo.imageKey} image={photo} />
      ))}
    </span>
    {caption && <span className="mpb-page-caption">{caption}</span>}
  </span>
);

Page.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.object).isRequired,
  caption: PropTypes.string,
  className: PropTypes.string,
};

const Hero = ({ facts, ctaRef }) => (
  <section className="lnd-hero mpb-hero">
    <Container className="mpb-hero-inner">
      <div className="mpb-hero-copy">
        <Breadcrumb items={BREADCRUMB} />
        <h1 className="lnd-h1 mpb-h1">Mini Photo Books</h1>
        <p className="lnd-lead mpb-lead">
          A little book for one good chapter. Turn a weekend away, a friendship, or everyday family
          moments into a 6&nbsp;×&nbsp;6-inch softcover photo book.
        </p>
        <div className="mpb-buy" ref={ctaRef}>
          <p className="mpb-price-row">
            <FromPrice price={facts.mini} className="mpb-price" />
            {facts.pages && <span className="mpb-included">{facts.pages} pages included</span>}
          </p>
          <ul className="mpb-hero-facts">
            <li>
              <PdpIcon name="ruler" size={16} />6 × 6 inches
            </li>
            <li>
              <PdpIcon name="bookOpen" size={16} />
              Up to {MAX_PAGES} pages
            </li>
            <li className="lnd-desk">
              <PdpIcon name="penLine" size={16} />
              Captions on cover and pages
            </li>
          </ul>
          <div className="mpb-hero-actions">
            <CreateButton location="hero" />
            <a
              href={`#${ID.IDEAS}`}
              className="mpb-link mpb-hero-ideas"
              onClick={track('ideas', 'hero', `#${ID.IDEAS}`)}
            >
              See mini book ideas
              <PdpIcon name="arrowDown" size={16} />
            </a>
          </div>
          <p className="mpb-note mpb-hero-note">
            Price before delivery and any applicable tax, both shown at checkout.
            {facts.sheetPrice && (
              <span className="lnd-desk">
                {' '}
                Extra pages are {facts.sheetPrice} per sheet (2 pages).
              </span>
            )}
          </p>
        </div>
      </div>
      <figure className="mpb-hero-photo">
        <Picture image={IMAGES.hero} sizes={HERO_SIZES} priority />
        <figcaption className="mpb-chip mpb-hero-chip">6 × 6 Softcover Photo Book</figcaption>
      </figure>
    </Container>
  </section>
);

Hero.propTypes = {
  facts: PropTypes.object.isRequired,
  ctaRef: PropTypes.object.isRequired,
};

const Ideas = () => (
  <section id={ID.IDEAS} className="mpb-section mpb-ideas">
    <Container>
      <div className="mpb-ideas-head">
        <div className="mpb-head">
          <p className="mpb-eyebrow">Mini book ideas</p>
          <h2 className="mpb-h2">One book for one chapter</h2>
        </div>
        <p className="mpb-intro">
          A small book is easiest to finish when it holds one story. Pick the chapter first, then
          keep only the photos that belong to it. These are ideas to borrow, not ready-made
          templates.
        </p>
      </div>

      <article className="mpb-feature">
        <div className="mpb-stage mpb-stage--cream mpb-feature-stage" aria-hidden="true">
          <Cover cover={COVERS.coast} className="mpb-feature-cover" />
          <span className="mpb-spread mpb-feature-spread">
            <Page photos={[IMAGES.beachWide]} caption="Saturday, Ocean Beach" />
            <Page photos={[IMAGES.beachSelfie]} className="mpb-page--full" />
          </span>
        </div>
        <div className="mpb-feature-copy">
          <h3 className="mpb-h3 mpb-feature-title">Weekend away</h3>
          <p className="mpb-feature-body">
            Choose a few moments from each day, then add the place and date.
          </p>
          <p className="mpb-label">What to pick</p>
          <ul className="mpb-checks">
            {PICK_LIST.map((item) => (
              <li key={item}>
                <PdpIcon name="check" size={16} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </article>

      <ul className="mpb-more">
        <li className="mpb-idea">
          <div className="mpb-stage mpb-idea-stage mpb-idea-stage--photo">
            <Picture image={IMAGES.baby} sizes="(min-width: 992px) 374px, calc(100vw - 40px)" />
            <span className="mpb-chip mpb-idea-chip">Printed 6 × 6 book</span>
          </div>
          <h3 className="mpb-h3">Baby’s everyday moments</h3>
          <p className="mpb-idea-body">
            Skip the milestone checklist. Keep the ordinary days you want to remember: naps, bath
            time, first foods, the walk to the park.
          </p>
        </li>
        {SMALL_IDEAS.map((idea) => (
          <li key={idea.key} className="mpb-idea">
            <div className={`mpb-stage mpb-stage--${idea.tone} mpb-idea-stage`} aria-hidden="true">
              <span className="mpb-spread mpb-idea-spread">
                {idea.pages.map((page) => (
                  <Page
                    key={page.image.imageKey}
                    photos={[page.image]}
                    caption={page.caption}
                    className={classNames({ 'mpb-page--full': page.tall })}
                  />
                ))}
              </span>
              <Cover cover={idea.cover} className="mpb-idea-cover" />
            </div>
            <h3 className="mpb-h3">{idea.title}</h3>
            <p className="mpb-idea-body">{idea.body}</p>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const Details = ({ facts }) => {
  const rows = [
    { icon: 'ruler', label: 'Size', value: '6 × 6 in (15.2 × 15.2 cm), square' },
    { icon: 'book', label: 'Cover', value: 'Softcover with your photo and title' },
    { icon: 'layers', label: 'Paper', value: 'Matte paper pages' },
    { icon: 'penLine', label: 'Captions', value: 'On the cover and under photos inside' },
    facts.pages && {
      icon: 'bookOpen',
      label: 'Pages',
      value: `${facts.pages} pages included, up to ${MAX_PAGES}`,
    },
    facts.sheetPrice && {
      icon: 'plus',
      label: 'Extra pages',
      value: `${facts.sheetPrice} per extra sheet. One sheet is 2 pages.`,
    },
  ].filter(Boolean);

  return (
    <section id={ID.DETAILS} className="mpb-section mpb-cream mpb-details">
      <Container className="mpb-details-inner">
        <figure className="mpb-details-photo">
          <div className="mpb-details-image">
            <Picture image={IMAGES.shelf} sizes="(min-width: 992px) 560px, calc(100vw - 40px)" />
          </div>
          <figcaption className="mpb-caption">
            <PdpIcon name="camera" size={14} className="lnd-desk" />A 6 × 6 Softcover Photo Book
            with a captioned cover
          </figcaption>
        </figure>
        <div className="mpb-details-copy">
          <div className="mpb-head">
            <p className="mpb-eyebrow">The 6 × 6 Softcover</p>
            <h2 className="mpb-h2">What the book is like</h2>
            <p className="mpb-intro">
              A square softcover book with matte paper pages. Your photo and title go on the cover,
              captions can go under any photo inside.
            </p>
          </div>
          <dl className="mpb-facts">
            {rows.map((row) => (
              <div key={row.label} className="mpb-fact">
                <PdpIcon name={row.icon} size={18} />
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
          <p className="mpb-sheets">
            <span className="mpb-sheet-diagram" aria-hidden="true">
              <span className="mpb-sheet mpb-sheet--back" />
              <span className="mpb-sheet mpb-sheet--front">1</span>
            </span>
            Pages are not sheets. Each sheet is printed front and back, so one extra sheet adds 2
            pages.
          </p>
          <ArrowLink href={HREF.SOFTCOVER} onClick={track('softcover', 'details', HREF.SOFTCOVER)}>
            See all Softcover Photo Book details
          </ArrowLink>
        </div>
      </Container>
    </section>
  );
};

Details.propTypes = {
  facts: PropTypes.object.isRequired,
};

const WebBox = () => (
  <div className="mpb-web">
    <p className="mpb-web-title">
      <PdpIcon name="monitorSmartphone" size={18} />
      Make it on the web
    </p>
    <p className="mpb-web-body">
      Works in the browser on a computer or phone. Upload your photos, arrange the pages, add
      captions, then preview the whole book before you order.
    </p>
    <ArrowLink href={HREF.GUIDE} onClick={track('guide', 'guide', HREF.GUIDE)}>
      Read the full guide: How to make a photo book
    </ArrowLink>
  </div>
);

const Guide = () => (
  <section id={ID.GUIDE} className="mpb-section mpb-guide">
    <Container className="mpb-guide-inner">
      <div className="mpb-guide-copy">
        <div className="mpb-head">
          <p className="mpb-eyebrow">Help choosing the photos</p>
          <h2 className="mpb-h2">Fewer photos, a better story</h2>
        </div>
        <ol className="mpb-advice">
          {ADVICE.map((item, index) => (
            <li key={item.title}>
              <span className="mpb-number" aria-hidden="true">
                {index + 1}
              </span>
              <span>
                <span className="mpb-advice-title">{item.title}</span>
                <span className="mpb-advice-body">{item.body}</span>
              </span>
            </li>
          ))}
        </ol>
        <div className="lnd-desk">
          <WebBox />
        </div>
      </div>

      <figure className="mpb-example">
        <div className="mpb-stage mpb-stage--cream mpb-example-stage">
          <span className="mpb-spread mpb-example-spread" aria-hidden="true">
            <Page photos={[IMAGES.dunes]} caption="Ocean Beach, May 2026" />
            <Page
              photos={[IMAGES.beachDog, IMAGES.coast]}
              caption="Mila and Juno"
              className="mpb-page--pair"
            />
          </span>
          <ul className="mpb-example-notes">
            {LAYOUT_NOTES.map((note) => (
              <li
                key={note.key}
                className={`mpb-chip mpb-example-note mpb-example-note--${note.key}`}
              >
                <PdpIcon name={note.icon} size={14} />
                <Responsive desktop={note.desktop} phone={note.phone} />
              </li>
            ))}
          </ul>
        </div>
        <figcaption className="mpb-caption">
          Example layout to show the idea. Pick from the layouts in the editor.
        </figcaption>
      </figure>

      <div className="lnd-phone">
        <WebBox />
      </div>
    </Container>
  </section>
);

const SCALE_BOOKS = [
  { key: 'mini', inches: '6 in · 15.2 cm', caption: '6 × 6 Softcover' },
  { key: 'large', inches: '8 in · 20.3 cm', caption: '8 × 8 Softcover' },
];

// Books and phone drawn to one scale that follows the available width (40 px per inch at most),
// see the CSS. The table next to it carries the same facts for screen readers.
const ToScale = () => (
  <div className="mpb-scale-box">
    <figure className="mpb-scale" aria-hidden="true">
      <span className="mpb-scale-item">
        <span className="mpb-scale-phone" />
      </span>
      {SCALE_BOOKS.map((book) => (
        <span
          key={book.key}
          className={`mpb-scale-item mpb-scale-book mpb-scale-book--${book.key}`}
        >
          <span className="mpb-scale-dimension">
            {book.inches}
            <span className="mpb-scale-bracket" />
          </span>
          <Cover cover={COVERS.coast} />
        </span>
      ))}
      <span className="mpb-scale-baseline" />
      <span className="mpb-scale-label mpb-scale-label--phone">
        <Responsive desktop="A phone, for scale" phone="Phone" />
      </span>
      {SCALE_BOOKS.map((book) => (
        <span key={book.key} className={`mpb-scale-label mpb-scale-label--${book.key}`}>
          {book.caption}
        </span>
      ))}
      <span className="mpb-scale-note">Books and phone drawn to the same scale.</span>
    </figure>
  </div>
);

const Sizes = ({ facts }) => {
  const table = (
    <table className="mpb-size-table">
      <caption className="sr-only">Softcover photo book sizes and starting prices</caption>
      <tbody>
        <tr className="mpb-size-row--mini">
          <th scope="row">6 × 6 in</th>
          <td>15.2 × 15.2 cm</td>
          <td>
            <FromPrice price={facts.mini} />
          </td>
        </tr>
        <tr>
          <th scope="row">8 × 8 in</th>
          <td>20.3 × 20.3 cm</td>
          <td>
            <FromPrice price={facts.large} />
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <section id={ID.SIZES} className="mpb-section mpb-sizes">
      <Container className="mpb-sizes-inner">
        <div className="mpb-sizes-copy">
          <div className="mpb-head">
            <p className="mpb-eyebrow">Size</p>
            <h2 className="mpb-h2">6 × 6 or 8 × 8?</h2>
            <p className="mpb-intro">
              Both are square softcover books. Pick 6 × 6 for one smaller story. Pick 8 × 8 when you
              want more room for each photo.
            </p>
          </div>
          <div className="mpb-sizes-table">
            {table}
            {facts.pages && (
              <p className="mpb-note">Starting prices include {facts.pages} pages.</p>
            )}
            <ArrowLink href={HREF.SOFTCOVER} onClick={track('softcover', 'sizes', HREF.SOFTCOVER)}>
              Explore softcover sizes
            </ArrowLink>
          </div>
        </div>
        <ToScale />
      </Container>
    </section>
  );
};

Sizes.propTypes = {
  facts: PropTypes.object.isRequired,
};

// Shape ProductFaq renders. `description` is HTML, the FAQPage JSON-LD gets it without tags.
const faqItems = (facts) =>
  [
    {
      caption: 'How big is a mini photo book?',
      description:
        'Our mini photo book is the 6 × 6-inch Softcover Photo Book, 15.2 × 15.2 cm. For more room on each page, there is also an 8 × 8-inch Softcover.',
    },
    facts.pages && {
      caption: 'How many pages can it have?',
      description: `Every book includes ${facts.pages} pages. You can add pages up to ${MAX_PAGES}.`,
    },
    {
      caption: 'Is this an album for inserting prints?',
      description:
        'No. Your photos are printed right onto the pages of the book. There are no sleeves or pockets to fill.',
    },
    {
      caption: 'How do I make one?',
      description: `Start on the web. Upload your photos, arrange them on the pages and add captions, then preview the whole book before you add it to your cart. <a href="${HREF.GUIDE}">How to make a photo book</a> walks through it step by step.`,
    },
    facts.mini &&
      facts.pages &&
      facts.sheetPrice && {
        caption: 'How much does it cost?',
        description: `${facts.mini.originalPrice ?? facts.mini.price} for the 6 × 6 book with ${facts.pages} pages. Extra pages are ${facts.sheetPrice} per sheet, and one sheet is 2 pages. Delivery and any applicable tax are added at checkout.`,
      },
    {
      caption: 'When will it arrive?',
      description: `It depends on the shipping option and your address. See current production and delivery times on our <a href="${HREF.SHIPPING}">Shipping page</a>, and the estimate at checkout.`,
    },
  ].filter(Boolean);

const Faq = ({ items }) => (
  <section id={ID.FAQ} className="mpb-section mpb-faq">
    <Container>
      <ProductFaq
        eyebrow="Mini photo book questions"
        title="Before you start"
        intro={false}
        items={items}
      />
    </Container>
  </section>
);

Faq.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const FinalCta = ({ facts, ctaRef }) => (
  <section className="mpb-section mpb-cream mpb-final" ref={ctaRef}>
    <Container className="mpb-final-inner">
      <div className="mpb-fan" aria-hidden="true">
        <Cover cover={COVERS.grandpa} className="mpb-fan-cover mpb-fan-cover--left" />
        <Cover cover={COVERS.friends} className="mpb-fan-cover mpb-fan-cover--right" />
        <Cover cover={COVERS.coast} className="mpb-fan-cover mpb-fan-cover--center" />
      </div>
      <h2 className="mpb-h2">Start your mini photo book</h2>
      <ul className="mpb-reminder lnd-desk">
        <li>
          <PdpIcon name="ruler" size={16} />6 × 6 in Softcover
        </li>
        {facts.mini && (
          <li>
            <PdpIcon name="tag" size={16} />
            <FromPrice price={facts.mini} />
          </li>
        )}
        {facts.pages && (
          <li>
            <PdpIcon name="bookOpen" size={16} />
            {facts.pages} pages included
          </li>
        )}
      </ul>
      <p className="mpb-reminder-phone lnd-phone">
        <span className="mpb-reminder-strong">
          6 × 6 in Softcover
          {facts.mini && (
            <>
              {' · '}
              <FromPrice price={facts.mini} />
            </>
          )}
        </span>
        {facts.pages && (
          <span>
            {facts.pages} pages included, up to {MAX_PAGES}
          </span>
        )}
      </p>
      <CreateButton location="final_cta" />
      <p className="mpb-note">Delivery and any applicable tax are added at checkout.</p>
    </Container>
  </section>
);

FinalCta.propTypes = {
  facts: PropTypes.object.isRequired,
  ctaRef: PropTypes.object.isRequired,
};

const MiniPhotoBooksPage = () => {
  const facts = useBookFacts();
  const heroCtaRef = useRef(null);
  const finalRef = useRef(null);
  const pastHeroCta = useScrolledPast(heroCtaRef);
  const finalInView = useInView(finalRef);
  const faq = faqItems(facts);
  const faqJsonLd = faq.map(({ caption, description }) => ({
    question: caption,
    answer: description.replace(/<[^>]+>/g, ''),
  }));
  // Two unbreakable parts, so a narrow bar wraps at the dot and never hides the amount.
  const stickyMeta = [
    facts.mini && `From ${facts.mini.price}`,
    facts.pages && `${facts.pages} pages`,
  ]
    .filter(Boolean)
    .map((part, index) => (
      <React.Fragment key={part}>
        {index > 0 && ' · '}
        <span className="mpb-nowrap">{part}</span>
      </React.Fragment>
    ));

  return (
    <>
      <Head
        fullTitle="Mini Photo Books — 6×6 Softcover | Parabo Press"
        description={
          facts.mini && facts.pages
            ? `Make a 6×6 mini photo book with your favorite photos. Softcover books start at ${facts.mini.originalPrice ?? facts.mini.price} with ${facts.pages} pages included. Explore ideas and create your own.`
            : 'Make a 6×6 mini photo book with your favorite photos. Explore ideas and create your own.'
        }
        image={getCloudinaryUrl({ ...IMAGES.hero, format: 'jpg' })}
        heroImage={getCloudinaryUrl(IMAGES.hero)}
        heroImageSrcSet={getCloudinarySrcSet(IMAGES.hero)}
        heroImageSizes={HERO_SIZES}
      />
      <ScriptMarkupBreadcrumb items={BREADCRUMB} host={SETTINGS.HOST} />
      <ScriptMarkupFaq items={faqJsonLd} />

      <Layout>
        <div className="lnd mpb">
          <Hero facts={facts} ctaRef={heroCtaRef} />
          <Ideas />
          <Details facts={facts} />
          <Guide />
          <Sizes facts={facts} />
          <Faq items={faq} />
          <FinalCta facts={facts} ctaRef={finalRef} />
          <NewsletterTile />
          <StickyBar
            visible={pastHeroCta && !finalInView}
            title="6 × 6 Softcover"
            meta={stickyMeta}
            href={HREF.EDITOR}
            label="Make a mini book"
            icon="arrowRight"
            image={<Picture image={IMAGES.shelf} sizes="44px" alt="" />}
            onClick={track('create', 'sticky', HREF.EDITOR)}
          />
        </div>
      </Layout>
    </>
  );
};

export default MiniPhotoBooksPage;
